<template>
  <div v-if="useWebrtc" class="tel-ui">
    <!-- In-call floating widget (draggable, non-blocking) — visible on EVERY
         page so the agent keeps working / navigating during a call. -->
    <transition name="fade">
      <div
        v-if="callActive && !suppressWidget"
        class="fixed z-[9999] select-none rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden"
        :class="[minimized ? 'w-[240px]' : 'w-[290px]', dragging ? '' : 'transition-all duration-150']"
        :style="widgetStyle"
      >
        <div
          class="relative bg-gradient-to-b from-slate-800 to-slate-900 cursor-move"
          :class="minimized ? 'px-4 py-2.5' : 'px-5 pt-3 pb-5 text-center'"
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <button
            type="button"
            class="absolute top-2 right-2 z-10 text-white/60 hover:text-white focus:outline-none"
            @click.stop="minimized = !minimized"
            @mousedown.stop
            @touchstart.stop
          >
            <Icon :icon="minimized ? 'mdi:chevron-up' : 'mdi:chevron-down'" class="text-xl" />
          </button>

          <template v-if="minimized">
            <div class="flex items-center gap-2 pr-6">
              <span
                class="w-2 h-2 rounded-full flex-shrink-0"
                :class="callStatus === 'in-call' ? 'bg-emerald-400' : 'bg-amber-400 animate-pulse'"
              ></span>
              <span class="text-sm font-medium text-white truncate" dir="ltr">
                {{ callNumber || $t('tel_call_customer') }}
              </span>
              <span v-if="callStatus === 'in-call'" class="text-xs text-white/70 ml-auto tabular-nums">
                {{ fmtTime(seconds) }}
              </span>
            </div>
          </template>
          <template v-else>
            <div
              class="flex h-12 w-12 mx-auto items-center justify-center rounded-full mb-2"
              :class="callStatus === 'ended' ? 'bg-red-500/20' : 'bg-white/10'"
            >
              <Icon
                :icon="callStatus === 'ended' ? 'mdi:phone-hangup' : 'mdi:phone-in-talk'"
                class="text-2xl"
                :class="callStatus === 'ended' ? 'text-red-300' : 'text-white'"
              />
            </div>
            <div class="text-sm font-semibold text-white truncate" dir="ltr">
              {{ callNumber || $t('tel_call_customer') }}
            </div>
            <div
              class="text-xs mt-0.5"
              :class="callStatus === 'ended' ? 'text-red-300 font-medium' : 'text-white/70'"
            >
              {{ callStatus === 'ended'
                  ? $t('tel_call_ended')
                  : (callStatus === 'in-call' ? fmtTime(seconds) : $t('tel_calling')) }}
            </div>
          </template>
        </div>

        <div v-if="!minimized" class="flex items-center justify-center gap-3 py-3 bg-white">
          <button
            type="button"
            class="flex h-11 w-11 items-center justify-center rounded-full border transition"
            :class="muted ? 'bg-amber-100 border-amber-300 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'"
            @click="toggleMute"
          >
            <Icon :icon="muted ? 'mdi:microphone-off' : 'mdi:microphone'" class="text-lg" />
          </button>
          <button
            type="button"
            class="flex h-12 w-12 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-700 active:scale-95 transition"
            @click="hangup"
          >
            <Icon icon="mdi:phone-hangup" class="text-xl" />
          </button>
        </div>
      </div>
    </transition>

    <!-- Inbound screen-pop — customer calling back, on ANY page. -->
    <transition name="fade">
      <div
        v-if="inboundActive"
        class="fixed z-[10001] w-[330px] max-w-[92vw] select-none rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden"
        :class="dragging ? '' : 'transition-all duration-150'"
        :style="inboundStyle"
      >
        <div
          class="bg-gradient-to-b from-emerald-600 to-emerald-700 px-5 py-3 text-white flex items-center gap-2 cursor-move"
          @mousedown="startDrag($event, 'inbound')"
          @touchstart="startDrag($event, 'inbound')"
        >
          <span class="flex h-9 w-9 items-center justify-center rounded-full bg-white/20 animate-pulse">
            <Icon icon="mdi:phone-incoming" class="text-xl" />
          </span>
          <div class="min-w-0">
            <div class="text-sm font-semibold">{{ $t('tel_inbound_call') }}</div>
            <div class="text-xs text-white/80 truncate" dir="ltr">{{ inboundCaller || '—' }}</div>
          </div>
        </div>

        <div class="px-5 py-4">
          <div v-if="inboundLoading" class="flex items-center gap-2 text-slate-500 text-sm py-2">
            <Icon icon="eos-icons:three-dots-loading" class="text-xl" />
            {{ $t('loading') }}
          </div>

          <div v-else-if="inboundOrder" class="space-y-1.5">
            <div class="flex items-center gap-2">
              <Icon icon="mdi:account-circle-outline" class="text-lg text-[#5743BE]" />
              <span class="text-sm font-semibold text-slate-800">
                {{ (inboundOrder.customer && inboundOrder.customer.fullName) || $t('tel_unknown_customer') }}
              </span>
            </div>
            <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
              <span v-if="inboundOrder.id" class="inline-flex items-center gap-1">
                <Icon icon="mdi:pound" /> {{ inboundOrder.id }}
              </span>
              <span v-if="inboundOrder.customer && inboundOrder.customer.phone" class="inline-flex items-center gap-1" dir="ltr">
                <Icon icon="mdi:phone-outline" /> {{ inboundOrder.customer.phone }}
              </span>
              <span v-if="inboundOrder.customer && inboundOrder.customer.city" class="inline-flex items-center gap-1">
                <Icon icon="mdi:map-marker-outline" /> {{ inboundOrder.customer.city }}
              </span>
            </div>
            <div
              v-if="inboundOrder.products && inboundOrder.products.length"
              class="flex items-center gap-2 mt-1.5 rounded-lg bg-slate-50 p-1.5"
            >
              <img
                v-if="inboundOrder.products[0].image"
                :src="inboundOrder.products[0].image"
                class="w-9 h-9 rounded object-cover border border-slate-200 flex-shrink-0"
                @error="$event.target.style.display = 'none'"
              />
              <span class="text-xs text-slate-700 truncate">
                {{ inboundOrder.products[0].name }}
                <span v-if="inboundOrder.products.length > 1" class="text-slate-400">
                  +{{ inboundOrder.products.length - 1 }}
                </span>
              </span>
            </div>
            <button
              type="button"
              class="mt-1 inline-flex items-center gap-1 text-xs font-medium text-[#5743BE] hover:underline"
              @click="openInboundOrder"
            >
              <Icon icon="mdi:open-in-new" /> {{ $t('tel_open_order') }}
            </button>
          </div>

          <div v-else class="text-sm text-slate-500 py-1">
            {{ $t('tel_inbound_no_match') }}
          </div>

          <div class="mt-4 flex items-center gap-2">
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition"
              @click="acceptInbound"
            >
              <Icon icon="mdi:phone" /> {{ $t('tel_answer') }}
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-white text-sm font-semibold bg-red-600 hover:bg-red-700 active:scale-95 transition"
              @click="rejectInbound"
            >
              <Icon icon="mdi:phone-hangup" /> {{ $t('tel_reject') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
const webrtcPhone = require("../../common/webrtcPhone");
import bus from "../../common/eventBus";
import softphoneState from "../../common/softphoneState";

export default {
  name: "GlobalSoftphone",
  props: {
    user: { type: Object, default: () => ({}) },
    idWharhouse: { type: Object, default: null },
  },
  data() {
    return {
      useWebrtc: false,
      registered: false,
      // Consecutive WSS/SIP registration failures — after a couple in a row we
      // flip softphoneState.connFailed so the UI shows "offline" (see onError).
      _regFailCount: 0,
      callActive: false,
      callStatus: "", // calling | in-call
      callNumber: "",
      inCall: false,
      muted: false,
      seconds: 0,
      timer: null,
      minimized: false,
      pos: { top: 96, left: 24 },
      hasPos: false,
      dragging: false,
      _dragOffset: { x: 0, y: 0 },
      _dragTarget: "call", // "call" | "inbound" — which widget is being dragged
      // inbound
      inboundPos: { top: 16, left: 16 },
      inboundHasPos: false,
      inboundActive: false,
      inboundCaller: "",
      inboundOrder: null,
      inboundLoading: false,
    };
  },
  computed: {
    widgetStyle() {
      return { top: this.pos.top + "px", left: this.pos.left + "px" };
    },
    inboundStyle() {
      return { top: this.inboundPos.top + "px", left: this.inboundPos.left + "px" };
    },
    // While the auto-dialer is waiting for the REAL client answer (agent leg
    // auto-answered, customer still ringing / AMD analyzing), hide the in-call
    // widget so the agent isn't shown a call for a voicemail/no-answer.
    suppressWidget() {
      return softphoneState.suppressWidget;
    },
  },
  mounted() {
    this.initSoftphone();
    bus.$on("softphone:outbound", this.onOutboundStart);
    bus.$on("softphone:expect", this.onExpectOutbound);
    // Call-button asks "is the line ready?" and waits for the answer (via the
    // provided callback) before dialing — so a dead/expired line is revived first.
    bus.$on("softphone:ensure-ready", async (cb) => {
      const ok = await this.ensureLineReady();
      if (typeof cb === "function") cb(ok);
    });
    // A browser refresh / tab-close mid-call must drop the customer leg (BYE)
    // instead of waiting for Asterisk's rtp_timeout.
    this._unload = () => {
      try { if (this.inCall) webrtcPhone.hangupWebrtc(); } catch (e) { /* ignore */ }
    };
    window.addEventListener("beforeunload", this._unload);
    window.addEventListener("pagehide", this._unload);
    // Heartbeat: keep the line alive so it can't silently expire between calls.
    // Every ~4 min (well under the usual SIP registration lifetime) renew the
    // REGISTER; if we're NOT registered anymore, do a FULL re-init (re-fetches a
    // fresh SIP secret — covers the case where the extension was recreated and
    // the old password no longer works). Skipped during a live call.
    this._hb = setInterval(() => {
      if (this.inCall) return;
      try {
        if (webrtcPhone.isWebrtcRegistered && webrtcPhone.isWebrtcRegistered()) {
          webrtcPhone.reRegisterWebrtc && webrtcPhone.reRegisterWebrtc();
        } else if (this.useWebrtc) {
          this.initSoftphone(); // full re-init with a fresh password
        }
      } catch (e) { /* ignore */ }
    }, 240000); // 4 min
  },
  beforeDestroy() {
    bus.$off("softphone:outbound", this.onOutboundStart);
    bus.$off("softphone:expect", this.onExpectOutbound);
    bus.$off("softphone:ensure-ready");
    window.removeEventListener("beforeunload", this._unload);
    window.removeEventListener("pagehide", this._unload);
    if (this._hb) { clearInterval(this._hb); this._hb = null; }
    if (this._endTimer) { clearTimeout(this._endTimer); this._endTimer = null; }
    this.stopTimer();
    try { webrtcPhone.stopWebrtcPhone(); } catch (e) { /* ignore */ }
  },
  methods: {
    // Called right before a click-to-call dials, so a call NEVER fires on a
    // silently-dead line ("PC ready" but the token/registration expired → popup
    // shows, customer never rings). If the line looks unregistered, do a full
    // re-init (fresh secret + REGISTER) and wait up to ~4s for it to come back.
    // Returns true if the line is registered and ready to place the call.
    async ensureLineReady() {
      if (!this.useWebrtc) return true; // non-WebRTC agent → nothing to check
      const alive = () => {
        try { return !!(webrtcPhone.isWebrtcRegistered && webrtcPhone.isWebrtcRegistered()); }
        catch (e) { return false; }
      };
      if (alive()) return true;
      // Not registered → re-init with a fresh password, then poll for "registered".
      try { await this.initSoftphone(); } catch (e) { /* ignore */ }
      for (let i = 0; i < 20; i++) {          // up to ~4s (20 × 200ms)
        if (alive()) return true;
        await new Promise((r) => setTimeout(r, 200));
      }
      return alive();
    },
    async initSoftphone() {
      try {
        const res = await this.$server.customPost("telephony/webrtcConfig", {});
        const c = res && res.content;
        // ok only for agents with a desktop extension; others → no softphone.
        if (!c || !c.ok) {
          this.useWebrtc = false;
          return;
        }
        this.useWebrtc = true;
        softphoneState.useWebrtc = true;
        webrtcPhone.initWebrtcPhone(c, {
          onRegistered: () => {
            this.registered = true;
            softphoneState.registered = true;
            // A successful register means the WSS path is healthy again — clear
            // any prior "can't connect" state and reset the failure counter.
            softphoneState.connFailed = false;
            this._regFailCount = 0;
          },
          onUnregistered: () => { this.registered = false; softphoneState.registered = false; },
          onIncoming: () => {
            // our click-to-call leg → show/keep the widget
            this.callActive = true;
            this.inCall = true;
            softphoneState.inCall = true;
            if (this.callStatus !== "in-call") this.callStatus = "calling";
            this.muted = false;
          },
          onConnected: () => {
            this.inCall = true;
            this.callActive = true;
            softphoneState.inCall = true;
            this.callStatus = "in-call";
            this.startTimer();
          },
          onEnded: () => {
            // The call ended — this fires whoever hung up. When the CLIENT hangs
            // up (GSM), our chan_dongle CLCC patch detects it and Asterisk sends
            // the BYE, so this arrives here too. Show a brief "call ended" state
            // in the widget (so the agent SEES the client dropped, instead of a
            // popup that just lingers) then auto-close it after ~1.5s.
            this.inCall = false;
            softphoneState.inCall = false;
            this.muted = false;
            this.stopTimer();
            this.inboundActive = false;
            this.callStatus = "ended";
            if (this._endTimer) clearTimeout(this._endTimer);
            this._endTimer = setTimeout(() => {
              this.callActive = false;
              this.callStatus = "";
              this._endTimer = null;
            }, 1500);
          },
          onInbound: (caller) => {
            // If the confirmation page is open, IT renders the incoming-call
            // screen-pop in-page (that's the dedicated call workspace) — hand the
            // call to it via the bus and do NOT show our floating popup, so the
            // agent never sees two. On any other page, show our floating popup so
            // a customer callback is never missed.
            if (softphoneState.inboundPageActive) {
              bus.$emit("softphone:inbound", caller);
            } else {
              this.handleInboundCall(caller);
            }
          },
          onError: (kind, err) => {
            // Surface mic / media failures to the shared state so any page (the
            // confirmation Call button) can show WHY a call didn't connect —
            // without the agent opening the browser console.
            if (kind === "getusermedia") {
              const name = (err && (err.name || (err.constructor && err.constructor.name))) || "";
              let code = "generic";
              if (name === "NotAllowedError" || name === "SecurityError") code = "mic-blocked";
              else if (name === "NotReadableError" || name === "AbortError") code = "mic-busy";
              else if (name === "NotFoundError" || name === "OverconstrainedError") code = "mic-missing";
              softphoneState.lastError = { kind: code, at: Date.now() };
            } else if (kind === "media") {
              // ICE/media failure → the call connected but audio can't flow
              // (NAT / firewall blocking RTP). Surface it so the agent retries.
              softphoneState.lastError = { kind: "no-audio", at: Date.now() };
            } else if (kind === "register") {
              // The WSS/SIP registration failed — the softphone could NOT reach
              // the PBX (agent off the office LAN / VPN down, PBX offline, or
              // :8089 blocked). JsSIP keeps retrying, so a single failure is not
              // conclusive; only after a few consecutive misses do we flip to a
              // hard "offline" state (clear message + disabled Call button) so a
              // brief startup blip doesn't scare the agent.
              this._regFailCount = (this._regFailCount || 0) + 1;
              if (this._regFailCount >= 2) {
                this.registered = false;
                softphoneState.registered = false;
                softphoneState.connFailed = true;
              }
            }
          },
        });
      } catch (e) {
        this.useWebrtc = false;
      }
    },

    // Outbound click-to-call was triggered elsewhere (confirmation page).
    onExpectOutbound() {
      try { webrtcPhone.expectOutboundLeg(); } catch (e) { /* ignore */ }
    },
    onOutboundStart(payload) {
      const number = (payload && payload.number) || "";
      softphoneState.lastError = null; // clear any previous mic/media error
      this.callNumber = number;
      this.muted = false;
      this.callStatus = "calling";
      this.callActive = true;
      this.ensurePos();
      try { webrtcPhone.expectOutboundLeg(); } catch (e) { /* ignore */ }
    },

    // ── in-call widget ──────────────────────────────────────────────
    startTimer() {
      this.stopTimer();
      this.seconds = 0;
      this.timer = setInterval(() => { this.seconds += 1; }, 1000);
    },
    stopTimer() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    },
    toggleMute() {
      this.muted = !this.muted;
      try { webrtcPhone.setWebrtcMuted(this.muted); } catch (e) { /* ignore */ }
    },
    hangup() {
      try { webrtcPhone.hangupWebrtc(); } catch (e) { /* ignore */ }
      this.callActive = false;
      this.inCall = false;
      softphoneState.inCall = false;
      this.stopTimer();
    },
    fmtTime(s) {
      s = Math.floor(Number(s) || 0);
      const m = Math.floor(s / 60);
      const r = s % 60;
      return `${m}:${String(r).padStart(2, "0")}`;
    },

    // ── drag ────────────────────────────────────────────────────────
    ensurePos() {
      if (this.hasPos) return;
      const width = 290;
      const left =
        typeof window !== "undefined" ? Math.max(16, window.innerWidth - width - 24) : 24;
      this.pos = { top: 96, left };
    },
    // Default the inbound pop to the top-right corner (numeric coords so it can
    // then be dragged); only until the agent drags it somewhere themselves.
    ensureInboundPos() {
      if (this.inboundHasPos) return;
      const width = 330;
      const left =
        typeof window !== "undefined" ? Math.max(16, window.innerWidth - width - 16) : 16;
      this.inboundPos = { top: 16, left };
    },
    startDrag(e, target) {
      this._dragTarget = target === "inbound" ? "inbound" : "call";
      const pos = this._dragTarget === "inbound" ? this.inboundPos : this.pos;
      const evt = e.touches ? e.touches[0] : e;
      this._dragOffset = { x: evt.clientX - pos.left, y: evt.clientY - pos.top };
      this.dragging = true;
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
      document.addEventListener("touchmove", this.onDrag, { passive: false });
      document.addEventListener("touchend", this.stopDrag);
    },
    onDrag(e) {
      if (!this.dragging) return;
      const evt = e.touches ? e.touches[0] : e;
      if (e.cancelable) e.preventDefault();
      const next = {
        left: Math.max(0, evt.clientX - this._dragOffset.x),
        top: Math.max(0, evt.clientY - this._dragOffset.y),
      };
      if (this._dragTarget === "inbound") {
        this.inboundPos = next;
        this.inboundHasPos = true;
      } else {
        this.pos = next;
        this.hasPos = true;
      }
    },
    stopDrag() {
      this.dragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDrag);
      document.removeEventListener("touchend", this.stopDrag);
    },

    // ── inbound ─────────────────────────────────────────────────────
    async handleInboundCall(caller) {
      this.inboundCaller = caller || "";
      this.inboundOrder = null;
      this.inboundLoading = true;
      this.inboundActive = true;
      this.ensureInboundPos();
      try {
        const country =
          (this.idWharhouse && this.idWharhouse.country) ||
          (this.user && this.user.countries && this.user.countries[0]) ||
          null;
        const res = await this.$server.customPost("telephony/inboundLookup", {
          phone: caller,
          country,
        });
        const c = (res && res.content) || {};
        if (c.found) this.inboundOrder = c;
      } catch (e) {
        /* keep accept/reject even if lookup fails */
      } finally {
        this.inboundLoading = false;
      }
    },
    acceptInbound() {
      try { webrtcPhone.answerWebrtc(); } catch (e) { /* ignore */ }
      this.inboundActive = false;
      this.inCall = true;
      softphoneState.inCall = true;
      this.callActive = true;
      this.callStatus = "in-call";
      this.callNumber = this.inboundCaller;
      this.muted = false;
      this.ensurePos();
      this.startTimer();
    },
    rejectInbound() {
      try { webrtcPhone.hangupWebrtc(); } catch (e) { /* ignore */ }
      this.inboundActive = false;
    },
    openInboundOrder() {
      if (this.inboundOrder && this.inboundOrder.orderId) {
        this.$router
          .push(`/orders/confirmation/${this.inboundOrder.orderId}`)
          .catch(() => {});
      }
    },
  },
};
</script>
